/**
 * Analytics Tracking Service
 *
 * Provides client-side event tracking with batching and reliability features:
 * - Event queue with automatic batching (sends every 5s or when queue reaches 10 events)
 * - sendBeacon on page unload for reliability
 * - Session ID generation
 * - Performance timing capture
 * - Scroll depth tracking
 * - Error tracking with stack traces
 */

import { getItem, setItem } from '../utils/localStorage.js';

// Chrome reports this benign notice once per animation frame while a layout
// keeps resizing; recording it flooded the endpoint (SUPPORT-924).
const RESIZE_OBSERVER_LOOP_MESSAGE = /^ResizeObserver loop/;

/**
 * Seconds to wait after a 429: the throttle middleware's Retry-After header,
 * or retry_after from the controller's own limiter.
 *
 * @param {Response} response
 * @returns {Promise<number>}
 */
async function retryAfterSeconds(response) {
    const header = Number(response.headers?.get('Retry-After'));
    if (header > 0) {
        return header;
    }

    try {
        const body = await response.json();
        if (Number(body?.retry_after) > 0) {
            return Number(body.retry_after);
        }
    } catch (e) {
        // Not JSON
    }

    return 60;
}

class AnalyticsTracking {
    constructor(options = {}) {
        this.options = {
            batchSize: options.batchSize || 10,
            // The endpoint rejects a request with more than 50 events.
            maxEventsPerRequest: options.maxEventsPerRequest || 50,
            flushInterval: options.flushInterval || 5000, // 5 seconds
            endpoint: options.endpoint || '/api/analytics/track',
            debug: options.debug || false,
            ...options,
        };

        this.queue = [];
        this.sessionId = this.getOrCreateSessionId();
        this.flushTimer = null;
        this.isInitialized = false;
        this.scrollDepthTracked = new Set();
        this.trackedErrorKeys = new Set();
        this.flushPausedUntil = 0;
        this.pageLoadTime = null;
        this.pageStartTime = Date.now();

        // Bind methods
        this.track = this.track.bind(this);
        this.flush = this.flush.bind(this);
        this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
        this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    }

    /**
     * Initialize the tracking service
     */
    init() {
        if (this.isInitialized) return;

        // Start flush timer
        this.startFlushTimer();

        // Add event listeners for page unload
        window.addEventListener('beforeunload', this.handleBeforeUnload);
        document.addEventListener(
            'visibilitychange',
            this.handleVisibilityChange
        );

        // Track page load time when available
        if (document.readyState === 'complete') {
            this.capturePageLoadTime();
        } else {
            window.addEventListener('load', () => this.capturePageLoadTime());
        }

        // Initialize scroll depth tracking
        this.initScrollTracking();

        // Initialize error tracking
        this.initErrorTracking();

        this.isInitialized = true;
    }

    /**
     * Get or create a session ID
     */
    getOrCreateSessionId() {
        const SESSION_KEY = '_analytics_session';
        const SESSION_DURATION = 30 * 60 * 1000; // 30 minutes

        try {
            const stored = getItem(SESSION_KEY);
            if (stored) {
                const { id, timestamp } = JSON.parse(stored);
                // Check if session is still valid
                if (Date.now() - timestamp < SESSION_DURATION) {
                    // Refresh timestamp
                    setItem(
                        SESSION_KEY,
                        JSON.stringify({ id, timestamp: Date.now() })
                    );
                    return id;
                }
            }
        } catch (e) {
            // Ignore localStorage errors
        }

        // Create new session
        const newId = this.generateSessionId();
        try {
            setItem(
                SESSION_KEY,
                JSON.stringify({ id: newId, timestamp: Date.now() })
            );
        } catch (e) {
            // Ignore localStorage errors
        }
        return newId;
    }

    /**
     * Generate a unique session ID
     */
    generateSessionId() {
        return 'xxxx-xxxx-xxxx-xxxx'.replace(/x/g, () => {
            return Math.floor(Math.random() * 16).toString(16);
        });
    }

    /**
     * Track an event
     * @param {string} eventType - The event type (from UserEventTypes enum)
     * @param {object} data - Additional event data
     */
    track(eventType, data = {}) {
        if (!eventType) {
            return;
        }

        const event = {
            event_type: eventType,
            data: {
                ...data,
                session_id: this.sessionId,
                url: window.location.href,
                referrer: document.referrer || null,
                user_agent: navigator.userAgent,
                screen_width: window.screen.width,
                screen_height: window.screen.height,
                viewport_width: window.innerWidth,
                viewport_height: window.innerHeight,
            },
            timestamp: new Date().toISOString(),
        };

        this.queue.push(event);

        // Flush if queue is full
        if (this.queue.length >= this.options.batchSize) {
            this.flush();
        }
    }

    /**
     * Track a page view
     * @param {string} pageName - Optional page name/title
     * @param {object} data - Additional data
     */
    trackPageView(pageName = null, data = {}) {
        this.pageStartTime = Date.now();
        this.scrollDepthTracked.clear();
        this.trackedErrorKeys.clear();

        this.track('page_view', {
            page_name: pageName || document.title,
            page_path: window.location.pathname,
            page_search: window.location.search,
            page_hash: window.location.hash,
            ...data,
        });
    }

    /**
     * Track a button or element click
     * @param {string} elementName - Name/identifier of the element
     * @param {object} data - Additional data
     */
    trackClick(elementName, data = {}) {
        this.track('button_click', {
            element_name: elementName,
            ...data,
        });
    }

    /**
     * Track modal open/close
     * @param {string} modalName - Name of the modal
     * @param {string} action - 'opened' or 'closed'
     * @param {object} data - Additional data
     */
    trackModal(modalName, action, data = {}) {
        this.track(action === 'opened' ? 'modal_opened' : 'modal_closed', {
            modal_name: modalName,
            ...data,
        });
    }

    /**
     * Track a search action
     * @param {string} query - The search query
     * @param {number} resultCount - Number of results
     * @param {string} searchType - Type of search (e.g., 'global', 'filter')
     * @param {object} data - Additional data
     */
    trackSearch(query, resultCount = null, searchType = 'global', data = {}) {
        this.track('search_performed', {
            search_query: query,
            result_count: resultCount,
            search_type: searchType,
            ...data,
        });
    }

    /**
     * Track a filter being applied
     * @param {string} filterName - Name of the filter
     * @param {any} filterValue - Value of the filter
     * @param {object} data - Additional data
     */
    trackFilter(filterName, filterValue, data = {}) {
        this.track('filter_applied', {
            filter_name: filterName,
            filter_value: filterValue,
            ...data,
        });
    }

    /**
     * Track a feature usage
     * @param {string} featureName - Name of the feature
     * @param {string} action - Action taken (e.g., 'used', 'enabled', 'disabled')
     * @param {object} data - Additional data
     */
    trackFeature(featureName, action, data = {}) {
        this.track('button_click', {
            feature_name: featureName,
            feature_action: action,
            ...data,
        });
    }

    /**
     * Track a client-side error
     * @param {Error} error - The error object
     * @param {object} context - Additional context
     */
    trackError(error, context = {}) {
        const errorMessage = error.message || String(error);
        const errorName = error.name || 'Error';

        // Once per page view for the same error from the same place: an error
        // raised on every frame or poll would otherwise flood the endpoint and
        // use up the member's rate limit. The stack and location keep two
        // failures that share a message apart.
        const errorKey = [
            errorName,
            errorMessage,
            error.stack || '',
            context.filename || '',
            context.lineno || '',
        ].join('|');
        if (this.trackedErrorKeys.has(errorKey)) {
            return;
        }
        this.trackedErrorKeys.add(errorKey);

        this.track('client_error', {
            error_message: errorMessage,
            error_stack: error.stack || null,
            error_name: errorName,
            ...context,
        });
    }

    /**
     * Track API response time
     * @param {string} endpoint - The API endpoint
     * @param {number} duration - Response time in milliseconds
     * @param {number} status - HTTP status code
     * @param {object} data - Additional data
     */
    trackApiTime(endpoint, duration, status, data = {}) {
        this.track('api_response_time', {
            api_endpoint: endpoint,
            response_time_ms: duration,
            status_code: status,
            ...data,
        });
    }

    /**
     * Capture page load performance metrics
     */
    capturePageLoadTime() {
        if (this.pageLoadTime !== null) return;

        try {
            const timing = performance.timing || {};
            const navigationStart = timing.navigationStart || 0;

            if (timing.loadEventEnd && navigationStart) {
                this.pageLoadTime = timing.loadEventEnd - navigationStart;

                this.track('page_load_time', {
                    load_time_ms: this.pageLoadTime,
                    dom_content_loaded_ms:
                        timing.domContentLoadedEventEnd - navigationStart,
                    dom_interactive_ms: timing.domInteractive - navigationStart,
                    first_byte_ms: timing.responseStart - navigationStart,
                    dns_lookup_ms:
                        timing.domainLookupEnd - timing.domainLookupStart,
                    tcp_connect_ms: timing.connectEnd - timing.connectStart,
                });
            }
        } catch (e) {
            // Performance API not available or error
        }
    }

    /**
     * Initialize scroll depth tracking
     */
    initScrollTracking() {
        const depths = [25, 50, 75, 100];
        let ticking = false;

        const checkScrollDepth = () => {
            const scrollTop =
                window.pageYOffset || document.documentElement.scrollTop;
            const docHeight = Math.max(
                document.body.scrollHeight,
                document.documentElement.scrollHeight
            );
            const winHeight = window.innerHeight;
            const scrollPercent = Math.round(
                (scrollTop / (docHeight - winHeight)) * 100
            );

            for (const depth of depths) {
                if (
                    scrollPercent >= depth &&
                    !this.scrollDepthTracked.has(depth)
                ) {
                    this.scrollDepthTracked.add(depth);
                    this.track('scroll_depth', {
                        depth_percent: depth,
                        page_path: window.location.pathname,
                    });
                }
            }

            ticking = false;
        };

        window.addEventListener(
            'scroll',
            () => {
                if (!ticking) {
                    window.requestAnimationFrame(checkScrollDepth);
                    ticking = true;
                }
            },
            { passive: true }
        );
    }

    /**
     * Initialize global error tracking
     */
    initErrorTracking() {
        window.addEventListener('error', (event) => {
            if (RESIZE_OBSERVER_LOOP_MESSAGE.test(event.message || '')) {
                return;
            }

            this.trackError(event.error || new Error(event.message), {
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
            });
        });

        window.addEventListener('unhandledrejection', (event) => {
            const error =
                event.reason instanceof Error
                    ? event.reason
                    : new Error(String(event.reason));
            this.trackError(error, {
                type: 'unhandled_promise_rejection',
            });
        });
    }

    /**
     * Start the flush timer
     */
    startFlushTimer() {
        if (this.flushTimer) {
            clearInterval(this.flushTimer);
        }
        this.flushTimer = setInterval(() => {
            if (this.queue.length > 0) {
                this.flush();
            }
        }, this.options.flushInterval);
    }

    /**
     * Handle page visibility change
     */
    handleVisibilityChange() {
        if (document.visibilityState === 'hidden') {
            // Track time on page when user leaves
            const timeOnPage = Date.now() - this.pageStartTime;
            this.track('time_on_page', {
                duration_ms: timeOnPage,
                page_path: window.location.pathname,
            });
            this.flush(true); // Use beacon
        }
    }

    /**
     * Handle before unload event
     */
    handleBeforeUnload() {
        this.flush(true); // Use beacon
    }

    /**
     * Flush the event queue
     * @param {boolean} useBeacon - Whether to use sendBeacon (for unload events)
     */
    async flush(useBeacon = false) {
        if (this.queue.length === 0) return;

        if (useBeacon && navigator.sendBeacon) {
            // Use sendBeacon for reliability during page unload. The queue goes
            // with the page, so send all of it, in batches the endpoint accepts,
            // even during a rate-limit pause.
            while (this.queue.length > 0) {
                const events = this.queue.splice(
                    0,
                    this.options.maxEventsPerRequest
                );
                const blob = new Blob([JSON.stringify({ events })], {
                    type: 'application/json',
                });

                if (!navigator.sendBeacon(this.options.endpoint, blob)) {
                    // If sendBeacon fails, put events back in queue
                    this.queue = [...events, ...this.queue];
                    return;
                }
            }

            return;
        }

        if (Date.now() < this.flushPausedUntil) return;

        const events = this.queue.splice(0, this.options.maxEventsPerRequest);

        const payload = JSON.stringify({ events });

        // Use fetch for normal requests
        try {
            // Get CSRF token
            const csrfToken = document.querySelector(
                'meta[name="csrf-token"]'
            )?.content;

            // Authenticated via the same-origin session cookie + CSRF
            // token; no bearer token (SUPPORT-663 / F-024).
            const headers = {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': csrfToken || '',
                Accept: 'application/json',
            };

            const response = await fetch(this.options.endpoint, {
                method: 'POST',
                headers,
                body: payload,
                credentials: 'same-origin',
            });

            // Rate limited: wait it out rather than send again every few
            // seconds. Nothing in a rejected batch was saved, so it goes back
            // in the queue, only once the pause is set so that a flush in the
            // meantime can't resend it early.
            if (response.status === 429) {
                const pauseMs = (await retryAfterSeconds(response)) * 1000;
                this.flushPausedUntil = Date.now() + pauseMs;
                this.queue = [...events, ...this.queue];
            }
        } catch (error) {
            // Put events back in queue for retry
            this.queue = [...events, ...this.queue];
        }
    }

    /**
     * Destroy the tracking service
     */
    destroy() {
        if (this.flushTimer) {
            clearInterval(this.flushTimer);
        }
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
        document.removeEventListener(
            'visibilitychange',
            this.handleVisibilityChange
        );
        this.flush();
        this.isInitialized = false;
    }
}

// Create singleton instance
let instance = null;

/**
 * Get the analytics tracking singleton instance
 * @param {object} options - Configuration options
 * @returns {AnalyticsTracking}
 */
export function getAnalytics(options = {}) {
    if (!instance) {
        instance = new AnalyticsTracking(options);
    }
    return instance;
}

/**
 * Initialize analytics tracking
 * @param {object} options - Configuration options
 * @returns {AnalyticsTracking}
 */
export function initAnalytics(options = {}) {
    const analytics = getAnalytics(options);
    analytics.init();
    return analytics;
}

export { AnalyticsTracking };
export default getAnalytics;
